import React from "react";
import { motion } from "framer-motion";
import { FaLightbulb, FaUsers, FaHandshake, FaLeaf } from "react-icons/fa";
import Navbar from "../components/Navbar";

const AboutHLC = () => {
  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-[#542757]/10 to-[#9b1f6c]/10 text-gray-800">
        {/* Hero Section */}
        <section className="relative flex flex-col items-center justify-center text-center px-6 py-20 bg-gradient-to-r from-[#542757] to-[#9b1f6c] text-white">
          <motion.h1
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl sm:text-5xl font-extrabold mb-4 mt-10"
          >
            About HLC
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="text-lg sm:text-xl max-w-2xl"
          >
            Lighting up spaces across Pakistan with innovation, quality, and sustainability.
          </motion.p>
        </section>

        {/* Company Overview */}
        <section className="max-w-6xl mx-auto px-6 py-16">
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl font-bold text-center text-[#542757] mb-8"
          >
            Who We Are
          </motion.h2>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="text-gray-700 text-center max-w-3xl mx-auto leading-relaxed"
          >
            At HLC, we are passionate about redefining lighting solutions in Pakistan. Since our inception, 
            we have dedicated ourselves to creating products that combine technology, efficiency, and style. 
            From commercial to residential spaces, our lights are designed to inspire and enhance every environment.
          </motion.p>
        </section>

        {/* Mission & Vision */}
        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 px-6">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-[#542757]/10 to-[#9b1f6c]/10 p-8 rounded-3xl shadow-lg"
            >
              <h3 className="text-2xl font-bold text-[#542757] mb-4">Our Mission</h3>
              <p className="text-gray-700 leading-relaxed">
                To illuminate lives with sustainable and smart lighting solutions that deliver 
                exceptional performance while caring for the planet and future generations.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-[#9b1f6c]/10 to-[#542757]/10 p-8 rounded-3xl shadow-lg"
            >
              <h3 className="text-2xl font-bold text-[#542757] mb-4">Our Vision</h3>
              <p className="text-gray-700 leading-relaxed">
                To be Pakistan’s most trusted name in lighting innovation, recognized globally for our 
                commitment to quality, creativity, and environmental responsibility.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-16 px-6 max-w-6xl mx-auto">
          <h3 className="text-3xl font-bold text-center text-[#542757] mb-12">
            Our Core Values
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <FaLightbulb className="text-[#542757] text-3xl mb-3" />,
                title: "Innovation",
                desc: "We continuously embrace new technologies to deliver modern lighting solutions.",
              },
              {
                icon: <FaUsers className="text-[#542757] text-3xl mb-3" />,
                title: "Teamwork",
                desc: "Our strength lies in collaboration and unity within our diverse team.",
              },
              {
                icon: <FaHandshake className="text-[#542757] text-3xl mb-3" />,
                title: "Integrity",
                desc: "We believe in transparency, honesty, and maintaining long-term relationships.",
              },
              {
                icon: <FaLeaf className="text-[#542757] text-3xl mb-3" />,
                title: "Sustainability",
                desc: "Eco-friendly practices are at the core of everything we design and produce.",
              },
            ].map((value, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05 }}
                className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl text-center transition-all"
              >
                {value.icon}
                <h4 className="font-semibold text-lg text-[#542757] mb-2">
                  {value.title}
                </h4>
                <p className="text-gray-600 text-sm">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Footer or CTA */}
        <section className="text-center py-16 bg-gradient-to-r from-[#542757] to-[#9b1f6c] text-white">
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-2xl sm:text-3xl font-bold mb-4"
          >
            Together, Let’s Brighten the Future ✨
          </motion.h3>
          <p className="max-w-2xl mx-auto text-sm sm:text-base">
            At HLC, we’re not just creating lights — we’re creating experiences that 
            inspire, connect, and transform spaces across Pakistan.
          </p>
        </section>
      </div>
    </>
  );
};

export default AboutHLC;
